import BasicButton from "components/General/BasicButton";
import { useNavigate } from "react-router-dom";

import { landTypes } from "utils/game-information-constants";
import InfoPopout from "components/Wrappers/InfoPopout";
import { LandToolTips } from "utils/ToolTipConst";

import { HIGHLIGHTS } from "utils/ToolTipConst";
import PopText from "components/Wrappers/PopText";

const standardClassname = "space-y-2 text-sm p4";
const huntingTutorialContent: React.ReactNode = (
  <div className={standardClassname}>
    <p className="text-2xl font-bold text-center mb-4">Hunting Tutorial</p>
    <p>
      Using the Hunting Tool, you can designate cells for hunting ducks in. The
      number of ducks hunted is partially impacted by random variability.
    </p>
    <p>
      To use the tool, simply click on the hunting action icon, then click on a{" "}
      <PopText recordName="cell" text="cell" recordType="generic" /> to mark it
      for hunting.
    </p>
    <p>On the map cells there are icons representing where the ducks were at the end of the previous year. In each cell is a series of pluses for each water management regime, which represents how many ducks will be in the cell if a particular regime is chosen <PopText recordName="townhall" text="at townhall" recordType="generic" />.</p>
    <p>
      <span className={HIGHLIGHTS.neutral}>
        Ducks prefer moderately wet biomes at a moderate water level.
      </span>{" "}
      In these places, they can be found in abundance. Due to the relative
      seclusion of the Private{" "}
      <PopText recordName="estate" text="Estate" recordType="generic" />, ducks
      are twice as abundant there.
    </p>
    <p className="text-lg font-bold text-center mb-2">Duck Hunting Bag Table</p>
    <p>
      In the table below, "wl" refers to the water level in the cell during
      winter.
    </p>
    <p>These values are all doubled for the private estate.</p>
    <div className="overflow-x-auto">
      <table className="min-w-full border border-gray-300 divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
              Biome
            </th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
              wl ≤ 0
            </th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
              0 &lt; wl ≤ 30
            </th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
              30 &lt; wl &lt; 70
            </th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
              wl ≥ 70
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          <tr>
            <td className="px-4 py-2">
              <PopText recordName="meadow" text="Meadow" recordType="land" />
            </td>
            <td className="px-4 py-2">0</td>
            <td className="px-4 py-2">16</td>
            <td className="px-4 py-2">4</td>
            <td className="px-4 py-2">0</td>
          </tr>
          <tr>
            <td className="px-4 py-2">
              <PopText
                recordName="meadow reed"
                text="Meadow Reed"
                recordType="land"
              />
            </td>
            <td className="px-4 py-2">0</td>
            <td className="px-4 py-2">16</td>
            <td className="px-4 py-2">4</td>
            <td className="px-4 py-2">0</td>
          </tr>

          <tr className="bg-gray-50">
            <td className="px-4 py-2">
              <PopText
                recordName="reed water"
                text="Reed Water"
                recordType="land"
              />
            </td>
            <td className="px-4 py-2">0</td>
            <td className="px-4 py-2">8</td>
            <td className="px-4 py-2">4</td>
            <td className="px-4 py-2">2</td>
          </tr>
          <tr className="bg-gray-50">
            <td className="px-4 py-2">
              <PopText
                recordName="open water"
                text="Open Water"
                recordType="land"
              />
            </td>
            <td className="px-4 py-2">0</td>
            <td className="px-4 py-2">16</td>
            <td className="px-4 py-2">8</td>
            <td className="px-4 py-2">4</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);
const actionTutorialContent: { [key: string]: React.ReactNode } = {
  clearing: (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">Clearing Tutorial</p>
      <p>
        Using the{" "}
        <PopText
          recordName="clearing"
          text="clearing tool"
          recordType="action"
        />
        , you can
        <span className={HIGHLIGHTS.positive}> transform the biome</span> of a
        cell. This is useful, becuase it can{" "}
        <span className={HIGHLIGHTS.positive}>improve the productivity </span>
        of a <PopText recordName="cell" text="cell" recordType="generic" />, or
        make it more useable for your{" "}
        <PopText recordName="role" text="role" recordType="generic" />.
      </p>
      <p>
        In order to use the clearing tool, click on the tool icon, and then
        click on the{" "}
        <PopText recordName="map" text="map" recordType="generic" /> where you
        want to clear. Only certain biomes may be cleared, and you must have
        enough <PopText recordName="money" text="money" recordType="generic" />{" "}
        to pay for the clearing.
      </p>
      <p>
        When the clearing tool is selected, the price of clearing each cell will
        be visible on the map.
      </p>
      <p className="text-lg font-bold text-center mb-2">Clearing Table</p>
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Current Biome
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Clearing Cost
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                New Biome
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-2">
                <PopText
                  recordName="reedbed"
                  text="Reedbed"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">100</td>
              <td className="px-4 py-2">
                <PopText
                  recordName="reed water"
                  text="Reed Water"
                  recordType="land"
                />
              </td>
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">
                <PopText
                  recordName="reed shrubs"
                  text="Reed Shrubs"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">200</td>
              <td className="px-4 py-2">
                <PopText
                  recordName="reedbed"
                  text="Reedbed"
                  recordType="land"
                />
              </td>
            </tr>
            <tr>
              <td className="px-4 py-2">
                <PopText
                  recordName="reed water"
                  text="Reed Water"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">200</td>
              <td className="px-4 py-2">
                <PopText
                  recordName="open water"
                  text="Open Water"
                  recordType="land"
                />
              </td>
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">
                <PopText
                  recordName="meadow shrubs"
                  text="Meadow Shrubs"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">200</td>
              <td className="px-4 py-2">
                <PopText recordName="meadow" text="Meadow" recordType="land" />
              </td>
            </tr>
            <tr>
              <td className="px-4 py-2">
                {" "}
                <PopText recordName="forest" text="Forest" recordType="land" />
              </td>
              <td className="px-4 py-2">400</td>
              <td className="px-4 py-2">
                <PopText recordName="meadow" text="Meadow" recordType="land" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Clearing some biomes may make other people angry. Clearing away
        reedbeds or reed waters may anger{" "}
        <PopText
          recordName="Reed Harvester"
          text="reed harvesters"
          recordType="role"
        />{" "}
        and{" "}
        <PopText
          recordName="Conservationist"
          text="conservationists"
          recordType="role"
        />
        , for example.
      </p>
      <p>
        • Some biomes are just less efficient until you clear them. Forests and
        meadow shrubs are much more efficient after clearing, with little
        downside.
      </p>
    </div>
  ),
  embank: (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">Embank Tutorial</p>
      <p>
        The{" "}
        <PopText recordName="embank" text="Embank Tool" recordType="action" />{" "}
        allows you to erect a dike around a{" "}
        <PopText
          recordName="management unit"
          text="Management Unit"
          recordType="generic"
        />
        . When this is performed, you select a{" "}
        <PopText
          recordName="water regime"
          text="Water Management Regime"
          recordType="generic"
        />
        {""}for the management unit. This becomes the exclusive water regime of
        this management unit, and it cannot be changed.
      </p>
      <p>
        This is potentially very useful, becuase useful patterns of flooding can
        be maintainted regardless of the regime chosen by the public at{" "}
        <PopText recordName="townhall" text="Townhall" recordType="generic" />.
      </p>
      <p>
        In order to use the tool, simply click on the embankment tool icon, then
        click on the management unit you wish to embank. A modal will pop up
        asking you which water management regime you want to use for it. While
        the tool is selected, the cost of embanking each management unil will be
        shown on the map. You must have enough{" "}
        <PopText recordName="money" text="money" recordType="generic" /> to pay
        for the embankment.
      </p>
      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Embanking is{" "}
        <span className={HIGHLIGHTS.negative}>extremely expensive</span>. It
        makes sense to coordinate with other roles in order to pay for an
        important dike.
      </p>
      <p>
        • If a management unit is enclosed away from the water by embanked
        management units,{" "}
        <span className={HIGHLIGHTS.negative}>
          it will be isolated from any water management regime
        </span>
        , and water levels will not change throughout the year. This could be
        <span className={HIGHLIGHTS.negative}> devastating</span> for the local
        environment.
      </p>
      <img src="/Images/WLregime.png"></img>
    </div>
  ),
  disembank: (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">Disembank Tutorial</p>
      <p>
        The{" "}
        <PopText
          recordName="disembank"
          text="Disembank Tool"
          recordType="action"
        />{" "}
        allows you to remove a dike around a{" "}
        <PopText
          recordName="management unit"
          text="Management Unit"
          recordType="generic"
        />
        . This means it will follow{" "}
        <PopText recordName="townhall" text="Townhall's" recordType="generic" />{" "}
        chosen{" "}
        <PopText
          recordName="water regime"
          text="Water Management Regime"
          recordType="generic"
        />{" "}
        for the management unit, as normal.
      </p>
      <p>
        This always costs half as much as it did to embank the managment unit in
        the first place.
      </p>
      <p>
        In order to use the tool, simply click on the disembankment tool icon,
        then click on the management unit you wish to remove the embankment
        from.
      </p>
    </div>
  ),
  cut: (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">Cut Tutorial</p>
      <p>
        The <PopText recordName="cut" text="Cutting Tool" recordType="action" />{" "}
        allows you to harvest reeds to make{" "}
        <PopText recordName="money" text="money" recordType="generic" />. This
        is the primary economic activity of the{" "}
        <PopText
          recordName="Reed Harvester"
          text="Reed Harvester"
          recordType="role"
        />
        . The profits for cutting are impacted by random variability in the weather.
      </p>
      <p>
        Reeds are always harvested during winter, but they{" "}
        <span className={HIGHLIGHTS.negative}>
          cannot be harvested if the water level of that cell is above 20cm
        </span>{" "}
        during the winter.
      </p>
      <p>
        To use the tool, simply click on the cutting action icon, then click on
        either a{" "}
        <PopText recordName="reedbed" text="Reedbed" recordType="land" /> or a{" "}
        <PopText recordName="reed water" text="Reed Water" recordType="land" />{" "}
        cell to mark it for cutting.
      </p>
      <p> Plusses will appear on each cuttable cell, shown for each possible <PopText recordName="water regime" text="water regime" recordType="generic" />. The more pluses a water regime has, the more wealth can be extracted from that cell.</p>
      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>• Reedbeds make twice as much money harvesting as Reed Water does.</p>
      <p>
        • If harvested 3 years in a row, Reedbeds can decay into Reed Water if
        the water level is high enough. Reed Water decays into{" "}
        <PopText recordName="open water" text="Open Water" recordType="land" />{" "}
        after only one year of cutting.
      </p>
      <p>
        • If the water level is above 0 in the spring, harvesting that cell will
        yeild a 33% bonus output.
      </p>
    </div>
  ),
  grazing: (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">Grazing Tutorial</p>
      <p>
        Using the{" "}
        <PopText recordName="grazing" text="Grazing Tool" recordType="action" />
        , you can select{" "}
        <PopText recordName="cell" text="cells" recordType="generic" /> for
        your cattle to graze on.
      </p>
      <p>
        To use the tool, simply click on the grazing action icon, then click on
        a cell to mark it for grazing. When this action is selected, the grazing
        value for each season will appear on each grazable cell, shown for each possible <PopText recordName="water regime" text="water regime" recordType="generic" />. The more pluses a water regime has, the more grazable land that cell has for cows.
      </p>
      <p>
        Each cow requires one unit of food per season. If a cow cannot be fed
        during a season, you will lose{" "}
        <PopText recordName="money" text="$10" recordType="generic" /> per cow
        season unfulfilled to purchase feed for the animal.
      </p>

      <p>
        <span className={HIGHLIGHTS.negative}>Cattle cannot graze if the water level is above 20 cm for that season!</span>
      </p>
      <p className="text-lg font-bold text-center mb-2">Grazing Values Table</p>
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Biome
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Spring
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Summer
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Fall
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Winter
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-2">
                <PopText recordName="meadow" text="Meadow" recordType="land" />
              </td>
              <td className="px-4 py-2">5</td>
              <td className="px-4 py-2">5</td>
              <td className="px-4 py-2">5</td>
              <td className="px-4 py-2">3</td>
              <td className="px-4 py-2">18</td>
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">
                <PopText
                  recordName="meadow shrubs"
                  text="Meadow Shrubs"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">3</td>
              <td className="px-4 py-2">3</td>
              <td className="px-4 py-2">2</td>
              <td className="px-4 py-2">1</td>
              <td className="px-4 py-2">9</td>
            </tr>
            <tr>
              <td className="px-4 py-2">
                <PopText
                  recordName="meadow reed"
                  text="Meadow Reed"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">4</td>
              <td className="px-4 py-2">4</td>
              <td className="px-4 py-2">4</td>
              <td className="px-4 py-2">3</td>
              <td className="px-4 py-2">15</td>
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">
                <PopText
                  recordName="reedbed"
                  text="Reedbed"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">3</td>
              <td className="px-4 py-2">3</td>
              <td className="px-4 py-2">2</td>
              <td className="px-4 py-2">2</td>
              <td className="px-4 py-2">10</td>
            </tr>
            <tr>
              <td className="px-4 py-2">
                <PopText
                  recordName="reed shrubs"
                  text="Reed Shrubs"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">2</td>
              <td className="px-4 py-2">1</td>
              <td className="px-4 py-2">0</td>
              <td className="px-4 py-2">0</td>
              <td className="px-4 py-2">3</td>
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">
                <PopText
                  recordName="reed water"
                  text="Reed Water"
                  recordType="land"
                />
              </td>
              <td className="px-4 py-2">2</td>
              <td className="px-4 py-2">1</td>
              <td className="px-4 py-2">1</td>
              <td className="px-4 py-2">0</td>
              <td className="px-4 py-2">4</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Reedbeds make decent grazing, but doing so will revert them to a
        meadow. If this is done on a large scale, it will{" "}
        <span className={HIGHLIGHTS.negative}>devastate the ecology</span> of
        the wetlands, and you will make everyone angry at you.
      </p>
      <p>
        • You only need to graze enough to feed your cows. Grazing on too many
        cells is wasteful and destructive
      </p>
      <p>
        • Reinvest your profits into buying more cattle. Once you have enough,
        you will make enough money that you will not need to worry about
        grazing.
      </p>
    </div>
  ),
  "create-nature-reserve": (
    <div className={standardClassname}>
      <p className="text-2xl font-bold text-center mb-4">
        Nature Reserve Tutorial
      </p>
      <p>
        Using the{" "}
        <PopText
          recordName="create-nature-reserve"
          text="Nature Reserve Tool"
          recordType="action"
        />
        , you can designate a{" "}
        <PopText
          recordName="management unit"
          text="Management Unit"
          recordType="action"
        />{" "}
        as a nature reserve.
      </p>
      <p>
        A Nature Reserve has no active effects.{" "}
        <span className={HIGHLIGHTS.neutral}>
          The rules and regulations surrounding the nature reserve must be
          negotiated with other players
        </span>
        . The consequences for violating those rules also must be negotiated. As
        a conservationist, you should especially try to use your nature reserves
        to protect bird populations.
      </p>
      <p>
        Despite this, the Nature Reserve is a very powerful tool. You may be
        surprised how often other players will rally around it. As the
        Conservationist, you are in a unique position to bring all of the{" "}
        <PopText recordName="roles" text="roles" recordType="generic" />{" "}
        together.
      </p>
      <p>
        To use this tool, simply click on its action icon. Then click on the
        management unit you wish to make into a reservation. You will need
        enough <PopText recordName="money" text="money" recordType="generic" />{" "}
        to do this.
      </p>
      <p className="text-lg font-bold text-center mb-2">Tips</p>
      <p>
        • Reservations are expensive for you, but not so much for others. Try to
        get money from other players, bring them together.
      </p>
      <p>
        • Roles in the Private{" "}
        <PopText recordName="estate" text="Estate" recordType="generic" /> tend
        to have more disposable income. They are often more likely to donate to
        you.
      </p>
      <p>
        • Try to negotiate with the public and private estate at the same time.
      </p>
      <p>
        • Other players may not realize that there are no programmed active
        effects in the nature reserve. Their vision is also limited to their
        half of the <PopText recordName="map" text="map" recordType="generic" />{" "}
        . A sly conservationist could use this to their advantage.
      </p>
      <p>
        • As a conservationist, you are tasked with maintaining the bird
        populations. A nature reserve is an excellent way to do that.
      </p>
    </div>
  ),
  "private-hunt": huntingTutorialContent,
  "public-hunt": huntingTutorialContent,
};

export default function ActionTutorial({ action }: { action: string }) {
  const lands = Object.values(landTypes).filter(
    (land) => land.name !== "culture"
  ); // culture does not exist in the game

  return (
    <div>
      {actionTutorialContent[action] ?? (
        <div>
          Sorry, no tutorial availible. Please read the tooltip for basic
          information.
        </div>
      )}
    </div>
  );
}
